/**
 * Loan schedule math for Progear Finance 2.0, module 1.7 (Laenud & palgafond).
 *
 * This is the ONE definition of how a loan's repayment schedule is computed
 * (replit.md master rule: one truth per number; L9: integer cents only). The
 * API materializes these rows into ordinary `cash_flow_entry` records; no
 * component ever recomputes an installment.
 *
 * Money discipline (L9):
 *  - All inputs and outputs are integer cents.
 *  - Floating point is used ONLY for the monthly-rate factor `(1 + i)^n` when
 *    deriving the fixed annuity payment, and for the per-row interest product
 *    `balance × i` — each is rounded to whole cents exactly once.
 *  - The running balance is reduced with integer subtraction, and the LAST
 *    installment absorbs the accumulated rounding residual, so the schedule
 *    always sums exactly to the principal. Golden-tested.
 *
 * Schedule types (UI labels Estonian; codes English per replit.md terminology):
 *  - `annuity`         — annuiteetgraafik: fixed total payment each month.
 *  - `equal_principal` — võrdse põhiosaga (lineaarne) graafik: fixed principal,
 *                        declining interest, declining total.
 *  - `bullet`          — bullet-laen: interest-only during the term, the whole
 *                        principal in the final installment.
 *  - `balloon`         — jääkväärtusega (liisingutüüpiline) graafik: annuity
 *                        payments amortize the principal down to a residual
 *                        value, which is then paid as part of the FINAL
 *                        installment (buyout planning view).
 *
 * Dates here are NOMINAL calendar days (monthly steps from the first payment
 * date, clamped to month length). The statutory "shift to the first working
 * day" rule is applied by the materializer via `firstWorkingDay` (calendar.ts)
 * so the nominal schedule stays reproducible and bank-statement-comparable.
 */

/** A loan repayment schedule shape. */
export type LoanScheduleType = 'annuity' | 'equal_principal' | 'bullet' | 'balloon';

/** All schedule types in canonical order (UI dropdown order). */
export const LOAN_SCHEDULE_TYPES = [
  'annuity',
  'equal_principal',
  'bullet',
  'balloon',
] as const satisfies readonly LoanScheduleType[];

/** Terms needed to derive a repayment schedule. All money in integer cents. */
export interface LoanTerms {
  /** Amount owed at the first-payment starting line, in integer cents (> 0). */
  principalCents: number;
  /** Nominal annual interest rate in integer basis points (e.g. 490 = 4,9%). */
  annualRateBps: number;
  /** Number of monthly installments (1 … 600). */
  termMonths: number;
  /** ISO day (`YYYY-MM-DD`) of installment #1; later rows step monthly. */
  firstPaymentDate: string;
  /** Repayment shape — see the module doc comment. */
  scheduleType: LoanScheduleType;
  /**
   * Balloon residual (jääkväärtus) in integer cents — REQUIRED for `balloon`
   * (0 < residual < principal), and must be omitted/0 for every other type.
   */
  residualValueCents?: number;
}

/** One installment (computed OR imported). All money in integer cents. */
export interface LoanInstallment {
  /** 1-based installment number. */
  seq: number;
  /** Nominal due day (ISO, not working-day-shifted — the materializer shifts). */
  dueDate: string;
  /** Principal component of the payment. */
  principalCents: number;
  /** Interest component of the payment. */
  interestCents: number;
  /**
   * Row-level fee component — present ONLY on rows imported from a contract
   * annex that folds a fee into an installment. Computed schedules never set
   * it (the LT-style one-off contract fee is a contract fact, not a row).
   */
  feeCents?: number;
  /** Total payment: principal + interest (+ fee where imported). */
  totalCents: number;
  /** Balance still owed after this installment (0 after the last row). */
  remainingCents: number;
}

/** Aggregates over a schedule, for editor preview footers. */
export interface LoanScheduleTotals {
  /** Sum of principal components — always exactly the principal. */
  principalCents: number;
  /** Sum of interest components. */
  interestCents: number;
  /** Sum of row-level fees (0 for computed schedules — they never carry one). */
  feeCents: number;
  /** Sum of total payments. */
  totalCents: number;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Maximum supported term, keeping the generation loop trivially bounded. */
export const MAX_TERM_MONTHS = 600;

function assertPositiveInt(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${label}: expected a positive integer, received ${value}`);
  }
}

/** Days in the given month (month is 1-based). */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Step an ISO day forward by whole months, clamping the day-of-month to the
 * target month's length (2026-01-31 + 1 month → 2026-02-28). Pure arithmetic,
 * no time zone involved — the ONE month-step rule for loan due dates.
 */
export function addMonthsClamped(iso: string, months: number): string {
  if (!ISO_DATE.test(iso)) {
    throw new RangeError(`addMonthsClamped: expected an ISO date (YYYY-MM-DD), received ${iso}`);
  }
  if (!Number.isSafeInteger(months) || months < 0) {
    throw new RangeError(`addMonthsClamped: expected a non-negative integer month count, received ${months}`);
  }
  const [y, m, d] = iso.split('-').map(Number);
  const zeroBased = m - 1 + months;
  const targetYear = y + Math.floor(zeroBased / 12);
  const targetMonth = (zeroBased % 12) + 1;
  const day = Math.min(d, daysInMonth(targetYear, targetMonth));
  const mm = String(targetMonth).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${targetYear}-${mm}-${dd}`;
}

/** Validate a full set of loan terms; throws RangeError on any bad field. */
export function assertLoanTerms(terms: LoanTerms): void {
  assertPositiveInt(terms.principalCents, 'principalCents');
  if (!Number.isSafeInteger(terms.annualRateBps) || terms.annualRateBps < 0) {
    throw new RangeError(
      `annualRateBps: expected a non-negative integer (basis points), received ${terms.annualRateBps}`,
    );
  }
  assertPositiveInt(terms.termMonths, 'termMonths');
  if (terms.termMonths > MAX_TERM_MONTHS) {
    throw new RangeError(
      `termMonths: expected at most ${MAX_TERM_MONTHS}, received ${terms.termMonths}`,
    );
  }
  if (!ISO_DATE.test(terms.firstPaymentDate)) {
    throw new RangeError(
      `firstPaymentDate: expected an ISO date (YYYY-MM-DD), received ${terms.firstPaymentDate}`,
    );
  }
  const [y, m, d] = terms.firstPaymentDate.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) {
    throw new RangeError(
      `firstPaymentDate: ${terms.firstPaymentDate} is not a real calendar day`,
    );
  }
  if (!LOAN_SCHEDULE_TYPES.includes(terms.scheduleType)) {
    throw new RangeError(`scheduleType: unknown type ${String(terms.scheduleType)}`);
  }
  const residual = terms.residualValueCents ?? 0;
  if (terms.scheduleType === 'balloon') {
    if (!Number.isSafeInteger(residual) || residual <= 0 || residual >= terms.principalCents) {
      throw new RangeError(
        `residualValueCents: balloon schedule requires an integer residual with 0 < residual < principal, received ${terms.residualValueCents}`,
      );
    }
  } else if (residual !== 0) {
    throw new RangeError(
      `residualValueCents: only a balloon schedule may carry a residual, received ${terms.residualValueCents} for ${terms.scheduleType}`,
    );
  }
}

/**
 * The fixed monthly payment (integer cents) that amortizes `principal` down to
 * `residual` over `n` months at monthly rate `i`. Float is used only for the
 * `(1 + i)^n` factor; the result is rounded to whole cents exactly once.
 * With `i === 0` this degenerates to an even split of the amortized amount.
 */
function fixedPaymentCents(principalCents: number, residualCents: number, monthlyRate: number, n: number): number {
  if (monthlyRate === 0) {
    return Math.round((principalCents - residualCents) / n);
  }
  const factor = Math.pow(1 + monthlyRate, n);
  return Math.round(((principalCents - residualCents / factor) * monthlyRate * factor) / (factor - 1));
}

/**
 * Compute the full repayment schedule for `terms`.
 *
 * Invariants (golden-tested):
 *  - exactly `termMonths` rows, monthly nominal due dates;
 *  - `Σ principalCents === terms.principalCents` for every type (the balloon
 *    residual is part of the FINAL row's principal);
 *  - `remainingCents` is strictly decreasing and ends at 0;
 *  - the last row absorbs all rounding drift (`totalCents` may differ from the
 *    nominal fixed payment by the accumulated residual).
 */
export function loanSchedule(terms: LoanTerms): LoanInstallment[] {
  assertLoanTerms(terms);
  const { principalCents, annualRateBps, termMonths, firstPaymentDate, scheduleType } = terms;
  const residualCents = terms.residualValueCents ?? 0;
  const monthlyRate = annualRateBps / 10000 / 12;
  const interestOn = (balanceCents: number): number => Math.round(balanceCents * monthlyRate);

  const rows: LoanInstallment[] = [];
  let balance = principalCents;

  const evenPrincipal = Math.floor(principalCents / termMonths);
  const fixedPayment =
    scheduleType === 'annuity' || scheduleType === 'balloon'
      ? fixedPaymentCents(principalCents, residualCents, monthlyRate, termMonths)
      : 0;

  for (let seq = 1; seq <= termMonths; seq += 1) {
    const last = seq === termMonths;
    const interestCents = interestOn(balance);
    let principalPart: number;
    if (last) {
      // The final installment always clears the remaining balance — this is
      // where every earlier per-row rounding lands, and (for balloon) where
      // the residual value is due.
      principalPart = balance;
    } else if (scheduleType === 'annuity' || scheduleType === 'balloon') {
      principalPart = fixedPayment - interestCents;
    } else if (scheduleType === 'equal_principal') {
      principalPart = evenPrincipal;
    } else {
      // bullet: interest-only until the final installment.
      principalPart = 0;
    }
    balance -= principalPart;
    rows.push({
      seq,
      dueDate: addMonthsClamped(firstPaymentDate, seq - 1),
      principalCents: principalPart,
      interestCents,
      totalCents: principalPart + interestCents,
      remainingCents: balance,
    });
  }
  return rows;
}

/** Sum a schedule's components for preview footers (one truth, not per-view). */
export function loanScheduleTotals(rows: readonly LoanInstallment[]): LoanScheduleTotals {
  let principalCents = 0;
  let interestCents = 0;
  let feeCents = 0;
  for (const row of rows) {
    principalCents += row.principalCents;
    interestCents += row.interestCents;
    feeCents += row.feeCents ?? 0;
  }
  return {
    principalCents,
    interestCents,
    feeCents,
    totalCents: principalCents + interestCents + feeCents,
  };
}

// ---------------------------------------------------------------------------
// Imported contract schedules (laenulepingu import)
// ---------------------------------------------------------------------------
//
// A lender's annex (LISA) is computed with the LENDER's day-count convention
// (often 30/360) and rounding — it will NEVER match our own annuity math to
// the cent, and it is the contractually binding truth. So imported rows are
// stored VERBATIM (pgfin.loan_schedule_row) and validated as a chain, never
// recomputed. This section is the ONE definition of that validation and of
// how stored rows become `LoanInstallment`s (master rule: one truth per
// number; consumers go through `loanInstallments`, not their own mapping).

/** How a loan's installments are derived. Mirrors pgfin.loan.schedule_source. */
export type LoanScheduleSource = 'computed' | 'contract';

/**
 * One schedule row imported 1:1 from a contract annex. All money in integer
 * cents. `balanceBeforeCents` is the outstanding principal BEFORE the row's
 * payment — the annex convention (the printed "jääk" column).
 */
export interface ImportedScheduleRow {
  /** 1-based, contiguous row number (fee-only annex rows are normalized OUT
   *  before numbering — the contract fee is a contract fact, not a row). */
  seq: number;
  /** Nominal due day (ISO, not working-day-shifted). */
  dueDate: string;
  principalCents: number;
  interestCents: number;
  /** Row-level fee — 0 unless the annex folds a fee into an installment. */
  feeCents: number;
  /** Total payment as printed: principal + interest + fee. */
  totalCents: number;
  /** Outstanding principal BEFORE this payment, as printed. */
  balanceBeforeCents: number;
}

function assertIsoCalendarDay(iso: string, label: string): void {
  if (!ISO_DATE.test(iso)) {
    throw new RangeError(`${label}: expected an ISO date (YYYY-MM-DD), received ${iso}`);
  }
  const [y, m, d] = iso.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) {
    throw new RangeError(`${label}: ${iso} is not a real calendar day`);
  }
}

function assertNonNegativeInt(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label}: expected a non-negative integer, received ${value}`);
  }
}

/**
 * Validate an imported schedule against the loan's terms. Throws RangeError
 * (with the failing row's seq) on the first violation. Checks, in order:
 *
 *  1. non-empty, at most MAX_TERM_MONTHS rows, and exactly `termMonths` rows;
 *  2. seq contiguity (1…n in order) — duplicates/gaps corrupt external_ids;
 *  3. real calendar due days, strictly increasing, row 1 = firstPaymentDate;
 *  4. per-row: integer cents, total > 0, principal + interest + fee = total
 *     (the row identity exactly as printed);
 *  5. the balance chain: row 1's balance-before = the principal, each next
 *     balance-before = previous minus its principal, and the final row clears
 *     the balance to exactly 0 (which also forces Σ principal = principal).
 *
 * What it deliberately does NOT check: our own interest/annuity math against
 * the annex figures — the lender's day-count convention differs by design.
 */
export function validateImportedSchedule(
  rows: readonly ImportedScheduleRow[],
  expect: { principalCents: number; termMonths: number; firstPaymentDate: string },
): void {
  assertPositiveInt(expect.principalCents, 'principalCents');
  assertPositiveInt(expect.termMonths, 'termMonths');
  assertIsoCalendarDay(expect.firstPaymentDate, 'firstPaymentDate');
  if (rows.length === 0) {
    throw new RangeError('imported schedule: expected at least one row');
  }
  if (rows.length > MAX_TERM_MONTHS) {
    throw new RangeError(
      `imported schedule: expected at most ${MAX_TERM_MONTHS} rows, received ${rows.length}`,
    );
  }
  if (rows.length !== expect.termMonths) {
    throw new RangeError(
      `imported schedule: termMonths is ${expect.termMonths} but the schedule has ${rows.length} rows — they must match`,
    );
  }
  let balance = expect.principalCents;
  let previousDate: string | null = null;
  for (const [index, row] of rows.entries()) {
    const label = `row ${index + 1}`;
    if (row.seq !== index + 1) {
      throw new RangeError(
        `${label}: expected seq ${index + 1} (contiguous from 1), received ${row.seq}`,
      );
    }
    assertIsoCalendarDay(row.dueDate, `${label} dueDate`);
    if (index === 0 && row.dueDate !== expect.firstPaymentDate) {
      throw new RangeError(
        `${label}: dueDate ${row.dueDate} must equal firstPaymentDate ${expect.firstPaymentDate}`,
      );
    }
    if (previousDate !== null && row.dueDate <= previousDate) {
      throw new RangeError(
        `${label}: dueDate ${row.dueDate} must be after the previous row's ${previousDate}`,
      );
    }
    previousDate = row.dueDate;
    assertNonNegativeInt(row.principalCents, `${label} principalCents`);
    assertNonNegativeInt(row.interestCents, `${label} interestCents`);
    assertNonNegativeInt(row.feeCents, `${label} feeCents`);
    assertPositiveInt(row.totalCents, `${label} totalCents`);
    if (row.principalCents + row.interestCents + row.feeCents !== row.totalCents) {
      throw new RangeError(
        `${label}: principal ${row.principalCents} + interest ${row.interestCents} + fee ${row.feeCents} !== total ${row.totalCents}`,
      );
    }
    if (row.balanceBeforeCents !== balance) {
      throw new RangeError(
        `${label}: balance before payment is ${row.balanceBeforeCents}, expected ${balance} (chain from the principal)`,
      );
    }
    if (row.principalCents > balance) {
      throw new RangeError(
        `${label}: principal ${row.principalCents} exceeds the outstanding balance ${balance}`,
      );
    }
    balance -= row.principalCents;
  }
  if (balance !== 0) {
    throw new RangeError(
      `imported schedule: final balance is ${balance}, expected 0 (Σ principal must equal the loan principal)`,
    );
  }
}

/**
 * Map validated imported rows to installments. `remainingCents` is derived
 * (balance-before minus principal) so every consumer sees the same shape a
 * computed schedule has. Call `validateImportedSchedule` first — this mapper
 * trusts the chain.
 */
export function installmentsFromImported(
  rows: readonly ImportedScheduleRow[],
): LoanInstallment[] {
  return rows.map((row) => ({
    seq: row.seq,
    dueDate: row.dueDate,
    principalCents: row.principalCents,
    interestCents: row.interestCents,
    ...(row.feeCents > 0 ? { feeCents: row.feeCents } : {}),
    totalCents: row.totalCents,
    remainingCents: row.balanceBeforeCents - row.principalCents,
  }));
}

/** One pasted deferred-tax schedule item: a due day + the FINAL amount owed
 *  that day (EMTA amounts fold interest in — the printed figure IS the debt). */
export interface TaxScheduleItem {
  /** Nominal due day (ISO). */
  dueDate: string;
  /** The row's final amount in integer cents (> 0). */
  cents: number;
}

/**
 * Build the verbatim schedule rows of a deferred tax debt (EMTA ajatamise
 * otsus) from its pasted (date, amount) pairs. The decision's amounts are
 * FINAL — interest is already folded in — so every row books its full amount
 * as principal (interest 0, fee 0) and the balance chain walks the decision
 * total down to exactly 0. Items are sorted by due day here (paste order is
 * presentation, not truth); duplicate days are rejected because the imported
 * chain requires strictly increasing dates. The output always satisfies
 * `validateImportedSchedule` against principal = Σ cents, termMonths = n,
 * firstPaymentDate = the earliest day — the ONE builder both the entry dialog
 * preview and the API save re-validate against (master rule: no component
 * assembles its own chain).
 */
export function buildTaxScheduleRows(items: readonly TaxScheduleItem[]): ImportedScheduleRow[] {
  if (items.length === 0) {
    throw new RangeError('buildTaxScheduleRows: expected at least one item');
  }
  if (items.length > MAX_TERM_MONTHS) {
    throw new RangeError(
      `buildTaxScheduleRows: expected at most ${MAX_TERM_MONTHS} items, received ${items.length}`,
    );
  }
  for (const [index, item] of items.entries()) {
    assertIsoCalendarDay(item.dueDate, `item ${index + 1} dueDate`);
    assertPositiveInt(item.cents, `item ${index + 1} cents`);
  }
  const sorted = [...items].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].dueDate === sorted[i - 1].dueDate) {
      throw new RangeError(
        `buildTaxScheduleRows: duplicate due day ${sorted[i].dueDate} — one row per day`,
      );
    }
  }
  let balance = 0;
  for (const item of sorted) {
    balance += item.cents;
  }
  return sorted.map((item, index) => {
    const row: ImportedScheduleRow = {
      seq: index + 1,
      dueDate: item.dueDate,
      principalCents: item.cents,
      interestCents: 0,
      feeCents: 0,
      totalCents: item.cents,
      balanceBeforeCents: balance,
    };
    balance -= item.cents;
    return row;
  });
}

/** Everything the installment provider needs to know about one loan. */
export interface LoanScheduleInput {
  /** Which source feeds the schedule (pgfin.loan.schedule_source). */
  scheduleSource: LoanScheduleSource;
  /** The loan's terms (always present — a contract loan still stores them). */
  terms: LoanTerms;
  /** Stored annex rows — REQUIRED for `contract`, forbidden for `computed`. */
  importedRows?: readonly ImportedScheduleRow[];
}

/**
 * THE installment provider: every consumer (materializer, editor preview,
 * detail view, balance derivation) gets a loan's installments from here —
 * never from `loanSchedule` or a stored-row mapping directly — so a
 * contract-sourced loan can never fork from a computed one.
 */
export function loanInstallments(input: LoanScheduleInput): LoanInstallment[] {
  if (input.scheduleSource === 'contract') {
    if (input.importedRows === undefined || input.importedRows.length === 0) {
      throw new RangeError(
        'loanInstallments: a contract-sourced loan requires its imported schedule rows',
      );
    }
    validateImportedSchedule(input.importedRows, {
      principalCents: input.terms.principalCents,
      termMonths: input.terms.termMonths,
      firstPaymentDate: input.terms.firstPaymentDate,
    });
    return installmentsFromImported(input.importedRows);
  }
  if (input.importedRows !== undefined) {
    throw new RangeError(
      'loanInstallments: a computed loan must not carry imported schedule rows',
    );
  }
  return loanSchedule(input.terms);
}

/**
 * Outstanding principal on `isoDate`: the balance after every installment due
 * on or before that day (installments must be non-empty and ordered — the
 * provider guarantees both). Before the first due date this is the full
 * starting balance. The ONE "kui palju on veel maksta" definition — option A:
 * pre-2026 history never materializes, so the calendar cannot answer this and
 * the loan detail derives it from here instead.
 */
export function loanBalanceOn(
  installments: readonly LoanInstallment[],
  isoDate: string,
): number {
  assertIsoCalendarDay(isoDate, 'isoDate');
  if (installments.length === 0) {
    throw new RangeError('loanBalanceOn: expected at least one installment');
  }
  const first = installments[0];
  let balance = first.remainingCents + first.principalCents;
  for (const row of installments) {
    if (row.dueDate > isoDate) break;
    balance = row.remainingCents;
  }
  return balance;
}

// ------------------------------------------------- installment entry identity

/**
 * The component parts an installment materializes into calendar entries as
 * (`loan:<id>:<seq>:<part>`). Display order is fixed: principal, interest, fee
 * — the same order the materializer emits them in.
 */
export const LOAN_INSTALLMENT_PARTS = ['principal', 'interest', 'fee'] as const;
export type LoanInstallmentPart = (typeof LOAN_INSTALLMENT_PARTS)[number];

/** Identity parsed from a materialized installment entry's external id. */
export interface LoanInstallmentEntryRef {
  loanId: string;
  /** 1-based installment number (the `<seq>` segment). */
  seq: number;
  part: LoanInstallmentPart;
}

const LOAN_INSTALLMENT_EXTERNAL_ID = /^loan:(.+):(\d+):(principal|interest|fee)$/;

/**
 * Parse a materialized INSTALLMENT entry key (`loan:<id>:<seq>:<part>`) into
 * its identity, or null for anything else (null, a non-loan key, or a loan-level
 * fact like `loan:<id>:fee` / `loan:<id>:drawdown` that has no `<seq>` segment).
 * The ONE definition of the key shape — the materializer builds it, every
 * consumer (the enriched-entry sibling cross-reference) parses it from here.
 */
export function parseLoanInstallmentExternalId(
  externalId: string | null,
): LoanInstallmentEntryRef | null {
  if (externalId === null) return null;
  const match = LOAN_INSTALLMENT_EXTERNAL_ID.exec(externalId);
  if (match === null) return null;
  const seq = Number(match[2]);
  // The materializer only emits seq >= 1 — a `:0:` key is corrupt, not an installment
  // (and would blow up loanInstallmentExternalIdPrefix's positive-int guard downstream).
  if (seq < 1) return null;
  return { loanId: match[1], seq, part: match[3] as LoanInstallmentPart };
}

/**
 * The shared external-id prefix of ONE installment's component entries
 * (`loan:<id>:<seq>:`) — a LIKE on `<prefix>%` finds every part of the same
 * payment and nothing else (the trailing `:` fences seq 1 from seq 10).
 */
export function loanInstallmentExternalIdPrefix(loanId: string, seq: number): string {
  assertPositiveInt(seq, 'seq');
  return `loan:${loanId}:${seq}:`;
}

// ---------------------------------------- linked-invoice settlement allocation

/**
 * ONE explicit allocation of a linked invoice's gross onto a specific loan
 * installment component (`loan:<id>:<seq>:<part>`). `allocationCents` is the
 * ceiling that this invoice may ever contribute to the component — set at link
 * time and immutable (mirrors the DB pgfin.loan_installment_invoice_allocation
 * row). Always a positive whole-cent magnitude.
 */
export interface InvoiceComponentAllocation {
  /** The component entry id (cash_flow_entry.id for the loan:<id>:<seq>:<part> row). */
  componentEntryId: string;
  /** Which part of the installment this allocation funds. */
  part: LoanInstallmentPart;
  /** The immutable per-component ceiling (invoice gross earmarked here), whole cents > 0. */
  allocationCents: number;
}

/** The derived settled amount one component receives from a linked invoice. */
export interface ComponentSettlementShare {
  componentEntryId: string;
  part: LoanInstallmentPart;
  /** Cents of the invoice's OWN settled cash that lands on this component (0 ≤ x ≤ allocationCents). */
  settledCents: number;
}

const PART_RANK: Record<LoanInstallmentPart, number> = {
  principal: 0,
  interest: 1,
  fee: 2,
};

const assertWholeInt = (value: number, label: string): void => {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(
      `allocateInvoiceSettlementAcrossComponents: ${label} must be whole cents, received ${value}`,
    );
  }
};

/**
 * Distribute the CASH a linked invoice has actually settled across its explicit
 * per-component allocations, deterministically and in whole cents (L9; master
 * rule — the ONE definition of how a linked invoice's real settlement propagates
 * to loan installment components).
 *
 * The design (settlement is DERIVED, never booked): linking an invoice books NO
 * payment or adjustment (an unpaid invoice leaves every component outstanding).
 * When the invoice later settles some cash (`invoiceSettledCents`, resolved by
 * the winning source's observation), THAT cash flows onto the components — but
 * never more than each component's immutable `allocationCents` ceiling, and never
 * more than the invoice has actually settled.
 *
 * Deterministic FILL ORDER (documented, not pro-rata — a partial payment on a
 * loan repayment invoice pays down principal first, then interest, then fee, the
 * same order the schedule materializes and the same order the RPC validates):
 *  1. sort allocations principal → interest → fee (ties broken by componentEntryId
 *     for a total, stable order — sibling parts never collide in practice);
 *  2. walk them, giving each `min(remaining invoice cash, this allocation ceiling)`;
 *  3. stop when the invoice's settled cash is exhausted.
 *
 * Guarantees (all asserted whole cents):
 *  - Σ settledCents ≤ invoiceSettledCents (never invents cash);
 *  - settledCents[i] ≤ allocationCents[i] (never exceeds a component ceiling);
 *  - exact cents preserved (integer arithmetic only — no rounding);
 *  - a zero-settled invoice yields all-zero shares (unpaid link = outstanding).
 *
 * @param invoiceSettledCents the invoice's OWN settled cash (winning-source
 *        observation), whole cents ≥ 0. Values beyond Σ allocationCents are
 *        clamped by the per-component ceilings (an overpaid invoice cannot
 *        settle more than it was earmarked for).
 * @param allocations the immutable per-component earmarks (each > 0).
 */
export function allocateInvoiceSettlementAcrossComponents(
  invoiceSettledCents: number,
  allocations: readonly InvoiceComponentAllocation[],
): ComponentSettlementShare[] {
  assertWholeInt(invoiceSettledCents, 'invoiceSettledCents');
  if (invoiceSettledCents < 0) {
    throw new RangeError(
      `allocateInvoiceSettlementAcrossComponents: invoiceSettledCents must be >= 0, received ${invoiceSettledCents}`,
    );
  }
  for (const a of allocations) {
    assertWholeInt(a.allocationCents, `allocationCents(${a.componentEntryId})`);
    if (a.allocationCents <= 0) {
      throw new RangeError(
        `allocateInvoiceSettlementAcrossComponents: allocationCents must be > 0 for ${a.componentEntryId}, received ${a.allocationCents}`,
      );
    }
  }

  // Deterministic total order: part rank, then componentEntryId lexical.
  const ordered = [...allocations].sort(
    (x, y) =>
      PART_RANK[x.part] - PART_RANK[y.part] ||
      x.componentEntryId.localeCompare(y.componentEntryId, 'en'),
  );

  let remaining = invoiceSettledCents;
  const shares: ComponentSettlementShare[] = [];
  for (const a of ordered) {
    const settledCents = Math.max(0, Math.min(remaining, a.allocationCents));
    remaining -= settledCents;
    shares.push({ componentEntryId: a.componentEntryId, part: a.part, settledCents });
  }
  return shares;
}

/**
 * Aggregate the DERIVED linked-invoice settlement onto components, across MANY
 * invoices linked to the same installment. Each invoice projects independently
 * (its own `invoiceSettledCents` fills its own allocations via
 * {@link allocateInvoiceSettlementAcrossComponents}); the per-component results
 * are then SUMMED across invoices (distinct invoices are additive cash — invoice
 * A settling €100 of principal and invoice B settling €50 of principal is €150
 * of principal settled, capped by each invoice's own allocation ceiling).
 *
 * Returns a Map keyed by componentEntryId → total derived settled cents. Callers
 * merge this with DIRECT same-source component observations using MAX (never sum)
 * so a component observed both directly and via a linked invoice from the SAME
 * source is not double-counted (that merge lives in the API settlement read, the
 * ONE place source observations are resolved).
 */
export function aggregateLinkedInvoiceSettlement(
  invoices: readonly {
    invoiceSettledCents: number;
    allocations: readonly InvoiceComponentAllocation[];
  }[],
): Map<string, number> {
  const byComponent = new Map<string, number>();
  for (const invoice of invoices) {
    const shares = allocateInvoiceSettlementAcrossComponents(
      invoice.invoiceSettledCents,
      invoice.allocations,
    );
    for (const share of shares) {
      byComponent.set(
        share.componentEntryId,
        (byComponent.get(share.componentEntryId) ?? 0) + share.settledCents,
      );
    }
  }
  return byComponent;
}
